import axios from '../axios'

export interface CarrierType {
	id: number
	name: string
	slug: string
}

export interface UploadResponse {
	filename: string
	checksum: string
	size: number
}

export interface VersionRecord {
	carrierType: string
	version: string
	filename: string
	checksum: string
	size: number
	releaseNotes?: string
	releaseDate?: string
	setAsLatest?: boolean
}

export interface DashboardStats {
	totalMachines: number
	success: number
	pending: number
	failed: number
	byLocation: Record<string, number>
	byCarrierType: Record<string, number>
	latestVersions: Record<string, string>
	recentErrors: Array<{
		machineId: string
		version: string
		error: string
	}>
}

export interface Machine {
	machineId: string
	version: string
	location: string
	carrierType: string
	lastStatus:
		| 'checking'
		| 'downloading'
		| 'installing'
		| 'success'
		| 'failed'
	lastSeen?: string
}

// Carrier types
export const fetchCarrierTypes = async (): Promise<CarrierType[]> => {
	const { data } = await axios.get('/api/v1/carrier-type')
	return data
}

export const createCarrierType = async (body: {
	name: string
	slug: string
}): Promise<CarrierType> => {
	const { data } = await axios.post('/api/v1/carrier-type', body)
	return data
}

// Upload binary
export const uploadBinary = async (
	file: File,
	onProgress?: (percent: number) => void
): Promise<UploadResponse> => {
	const form = new FormData()
	form.append('file', file)
	const { data } = await axios.post('/api/v1/updates/upload', form, {
		headers: { 'Content-Type': 'multipart/form-data' },
		onUploadProgress: (e) => {
			if (onProgress && e.total) {
				onProgress(Math.round((e.loaded * 100) / e.total))
			}
		}
	})
	return data
}

// Register version
export const registerVersion = async (
	body: VersionRecord
): Promise<void> => {
	await axios.post('/api/v1/updates/versions', body)
}

// List versions
export const fetchVersions = async (
	carrierType?: string
): Promise<VersionRecord[]> => {
	const params = carrierType ? { carrierType } : {}
	const { data } = await axios.get('/api/v1/updates/versions', { params })
	return data
}

// Dashboard
export const fetchDashboard = async (): Promise<DashboardStats> => {
	const { data } = await axios.get('/api/v1/updates/dashboard')
	return data
}

// Machines
export const fetchMachines = async (params?: {
	status?: string
	carrierType?: string
}): Promise<Machine[]> => {
	const { data } = await axios.get('/api/v1/updates/machines', { params })
	return data
}

// Rollback
export const rollback = async (body: {
	carrierType: string
	version: string
	machineIds?: string[]
}): Promise<void> => {
	await axios.post('/api/v1/updates/rollback', body)
}
